import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from '@tanstack/react-router'
import { useTranslations } from '@/hooks/useTranslations'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormSection } from '@/components/common'
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Switch,
} from '@/components/ui'
import { toast } from '@/components/common/Toast'
import { useUnsaveWarningModal } from '@/components/common/Modals'
import { useCreateRole, useGetPermissionList, useUpdateRole } from '@/modules/role/services/role'
import { max } from 'lodash'

type Permission = {
  id: string
  name: string
  description: string | null
  key: string
  actions: string[] | null
}

type RoleFormProps = {
  action: 'create' | 'edit'
  id?: string
  data?: {
    name: string
    description?: string
    permissions: string[]
  }
}

type PermissionGroup = 'user' | 'role' | 'bill' | 'customer' | 'size' | 'cycle' | 'other' | 'home'

const getInitData = () => ({
  name: '',
  description: '',
  permissions: [] as string[],
})

export const RoleForm = ({ action, data, id }: RoleFormProps) => {
  const isCreate = action === 'create'
  const isEdit = action === 'edit'

  const cancelButtonClick = useRef(false)
  const { UnsaveWarningModal, showUnsaveWarningModal } = useUnsaveWarningModal()

  const t = useTranslations('admin.role_management.form')
  const router = useRouter()

  const { data: permissionList } = useGetPermissionList()
  const { mutateAsync: createRole, isLoading: isCreating } = useCreateRole()
  const { mutateAsync: updateRole, isLoading: isUpdating } = useUpdateRole()
  const formSchema = z.object({
    name: z.string().min(1, t('role_information.name.validation.empty')),
    description: z.string().min(1, t('role_information.description.validation.empty')),
    maxLogin: z.string().optional(),
    permissions: z.array(z.string()).min(1, t('permissions.validation.required')),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: isEdit ? data : getInitData(),
  })

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = form

  useEffect(() => {
    if (isEdit) {
      reset(data)
    }
  }, [isEdit, data])

  const handleFormSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      const payload = {
        name: data.name,
        description: data.description,
        status: 'active',
        key: "admin",
        max_drivice: data.maxLogin ? parseInt(data.maxLogin) : 1,
        permissionIds: permissionList?.data
          ?.filter(permission => data.permissions.includes(permission.id))
          .map(permission => permission.id) || []
      }

      if (isEdit) {
        await updateRole({ ...payload, id: id as string })
      } else {
        await createRole(payload)
      }
      toast.toastSuccess('Role saved successfully')
      router.navigate({ to: '/admin/role', replace: true })
    } catch (e) {
      if (e instanceof Error) {
        toast.toastFailed(e.message)
      }
    }
  }

  useEffect(() => {
    if (permissionList?.data) {
      const permissions = permissionList?.data?.filter(permission => permission.default).map(permission => permission.id)
      setValue('permissions', permissions || [], {
        shouldValidate: true,
      })
    }
  }, [permissionList?.data])

  useEffect(() => {
    const onBackButtonEvent = (e: Event) => {
      e.preventDefault()

      if (!cancelButtonClick.current) {
        if (window.confirm("คุณต้องการออกจากหน้านี้หรือไม่? การเปลี่ยนแปลงที่ยังไม่ได้บันทึกจะสูญหาย")) {
          cancelButtonClick.current = true
          setTimeout(() => {
            router.navigate({ to: '/admin/role', replace: true })
          }, 250)
        } else {
          cancelButtonClick.current = false
        }
      }
    }

    window.history.pushState(null, '', window.location.pathname)
    window.addEventListener('popstate', onBackButtonEvent)

    return () => {
      window.removeEventListener('popstate', onBackButtonEvent)
    }
  }, [])

  const handlePermissionChange = (permissionId: string, checked: boolean) => {
    const currentPermissions = form.getValues('permissions')
    if (checked) {
      form.setValue('permissions', [...currentPermissions, permissionId], {
        shouldValidate: true,
      })
    } else {
      form.setValue('permissions', currentPermissions.filter(id => id !== permissionId), {
        shouldValidate: true,
      })
    }
  }

  const getGroupTranslation = (group: string): PermissionGroup => {
    const validGroups: PermissionGroup[] = ['user', 'role', 'bill', 'customer', 'size', 'cycle', 'other', 'home']
    return validGroups.includes(group as PermissionGroup) ? (group as PermissionGroup) : 'other'
  }


  return (
    <Form {...form}>
      <form
        className="space-y-8"
        onSubmit={form.handleSubmit(handleFormSubmit)}
      >
        <FormSection title={t('role_information.title')}>
          <div className="flex flex-col space-y-8">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="flex gap-4 flex-col lg:flex-row">
                  <div className="w-60 space-y-2">
                    <FormLabel required>{t('role_information.name.label')}</FormLabel>
                  </div>
                  <div className="flex-1">
                    <FormControl>
                      <Input
                        className="lg:max-w-96"
                        placeholder={t('role_information.name.placeholder')}
                        disabled={isCreating}
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="px-3 mt-2" />
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="flex gap-4 flex-col lg:flex-row">
                  <div className="w-60 space-y-2">
                    <FormLabel required>{t('role_information.description.label')}</FormLabel>
                  </div>
                  <div className="flex-1">
                    <FormControl>
                      <Input
                        className="lg:max-w-96"
                        placeholder={t('role_information.description.placeholder')}
                        disabled={isCreating}
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="px-3 mt-2" />
                  </div>
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection title={"กำหนดการ login"}>
          <div className="flex flex-col space-y-4">
            <FormField
              control={form.control}
              name="maxLogin"
              render={({ field }) => (
                <FormItem className="flex gap-4 flex-col lg:flex-row">
                  <div className="w-60 space-y-2">
                    <FormLabel required>จำกัดจำนวนการเข้าสู่ระบบ</FormLabel>
                  </div>
                  <div className="flex-1">
                    <FormControl>
                      <Input
                        className="lg:max-w-96"
                        placeholder={"กรอกจำนวนการเข้าสู่ระบบสูงสุด (0 = ไม่จำกัด)"}
                        disabled={isCreating}
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="px-3 mt-2" />
                  </div>
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection title={t('permissions.title')}>
          <div className="w-full space-y-6">
            {permissionList?.data && Object.entries(
              permissionList.data.reduce((acc, permission) => {
                const group = permission.group || 'other'
                if (!acc[group]) {
                  acc[group] = []
                }
                acc[group].push(permission)
                return acc
              }, {} as Record<string, typeof permissionList.data>)
            ).map(([group, permissions]) => (
              <div key={group} className="space-y-4">
                <h2 className="text-lg font-semibold">{t(`permissions.groups.${getGroupTranslation(group)}`)}</h2>
                {permissions.map((permission) => (
                  <div key={permission.id} className="border rounded-lg p-4 lg:max-w-[50%]">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">{permission.name}</h3>
                        {permission.description && (
                          <p className="text-sm text-gray-500">{permission.description}</p>
                        )}
                      </div>
                      <Switch
                        disabled={permission.default}
                        checked={form.watch('permissions')?.includes(permission.id)}
                        onCheckedChange={(checked) => handlePermissionChange(permission.id, checked)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
            {
              form.formState.errors.permissions && (
                <p className="text-red-500">{form.formState.errors.permissions.message}</p>
              )
            }
          </div>
        </FormSection>

        <div className="flex justify-end mobile:justify-center mobile:w-full gap-4">
          <Button
            type="button"
            variant="ghost"
            disabled={isCreating}
            className="text-t-secondary mobile:w-full"
            onClick={() => showUnsaveWarningModal()}
          >
            {t('button.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={isCreating}
            className="mobile:w-full"
          >
            {t('button.save')}
          </Button>
        </div>
        <UnsaveWarningModal onDiscard={() => router.navigate({ to: '/admin/role' })} />
      </form>
    </Form>
  )
}
